export const SSO_LOGIN_MODES = ["standard", "sso-first"] as const;
export type SsoLoginMode = (typeof SSO_LOGIN_MODES)[number];

const DEFAULT_SSO_LOGIN_MODE: SsoLoginMode = "standard";

const AUTHORIZATION_URL_BY_PROTOCOL = {
    saml2: "/api/saml2/login/",
    cas3: "/api/cas3/login/",
    oauth: "/api/auth/login/",
} as const;

export type SsoProtocol = keyof typeof AUTHORIZATION_URL_BY_PROTOCOL;

export const SSO_PROTOCOLS = Object.keys(AUTHORIZATION_URL_BY_PROTOCOL) as SsoProtocol[];

const isSsoProtocol = (segment: string): segment is SsoProtocol => {
    return segment in AUTHORIZATION_URL_BY_PROTOCOL;
};

type SsoConnectorRoute = {
    protocol: SsoProtocol;
    providerName: string;
    authorizationUrl: string;
};

export type SsoConnector = SsoConnectorRoute & {
    label: string;
    logo: string | undefined;
};

export const parseSsoConnectorPath = (path: string | undefined): SsoConnectorRoute | null => {
    const segments = (path ?? "")
        .trim()
        .split("/")
        .filter((segment) => segment !== "");

    if (segments.length !== 2) {
        return null;
    }

    const [protocol, providerName] = segments;
    if (!isSsoProtocol(protocol)) {
        return null;
    }

    return {
        protocol,
        providerName,
        authorizationUrl: AUTHORIZATION_URL_BY_PROTOCOL[protocol],
    };
};

export const buildSsoConnectorPath = (protocol: SsoProtocol | null, providerName: string): string | null => {
    const trimmedProviderName = providerName.trim();

    if (protocol === null || trimmedProviderName === "" || trimmedProviderName.includes("/")) {
        return null;
    }

    return `/${protocol}/${trimmedProviderName}`;
};

export const resolveSsoLoginMode = (mode: string | undefined): SsoLoginMode => {
    const found = SSO_LOGIN_MODES.find((knownMode) => knownMode === mode?.trim());

    return found ?? DEFAULT_SSO_LOGIN_MODE;
};

type BuildSsoConnectorProps = {
    path: string | undefined;
    label: string | undefined;
    logo: string | undefined;
};
export const buildSsoConnector = (props: BuildSsoConnectorProps): SsoConnector | null => {
    const { path, label, logo } = props;

    const route = parseSsoConnectorPath(path);
    if (route === null) {
        return null;
    }

    const trimmedLabel = label?.trim();
    const trimmedLogo = logo?.trim();

    return {
        ...route,
        label: trimmedLabel !== undefined && trimmedLabel !== "" ? trimmedLabel : route.providerName,
        logo: trimmedLogo !== undefined && trimmedLogo !== "" ? trimmedLogo : undefined,
    };
};

export const CONNECTOR_LABEL_PREFIX = "sso-connector-label:";
export const CONNECTOR_LOGO_PREFIX = "sso-connector-logo:";

export type ConnectorParamKind = "label" | "logo";

const PREFIX_BY_KIND: Record<ConnectorParamKind, string> = {
    label: CONNECTOR_LABEL_PREFIX,
    logo: CONNECTOR_LOGO_PREFIX,
};

export const buildConnectorParamKey = (
    kind: ConnectorParamKind,
    protocol: SsoProtocol,
    providerName: string,
): string | null => {
    const trimmedProviderName = providerName.trim();

    if (trimmedProviderName === "" || trimmedProviderName.includes("/")) {
        return null;
    }

    return `${PREFIX_BY_KIND[kind]}${protocol}:${trimmedProviderName}`;
};

type ParsedConnectorParamKey = SsoConnectorRoute & { kind: ConnectorParamKind };

export const parseConnectorParamKey = (key: string): ParsedConnectorParamKey | null => {
    const kind = (Object.keys(PREFIX_BY_KIND) as ConnectorParamKind[]).find((candidate) =>
        key.startsWith(PREFIX_BY_KIND[candidate]),
    );

    if (kind === undefined) {
        return null;
    }

    const suffix = key.slice(PREFIX_BY_KIND[kind].length);
    const separatorIndex = suffix.indexOf(":");
    if (separatorIndex === -1) {
        return null;
    }

    const protocol = suffix.slice(0, separatorIndex);
    const providerName = suffix.slice(separatorIndex + 1);
    if (!isSsoProtocol(protocol) || providerName === "" || providerName.includes("/")) {
        return null;
    }

    return {
        kind,
        protocol,
        providerName,
        authorizationUrl: AUTHORIZATION_URL_BY_PROTOCOL[protocol],
    };
};

type ApplicationParameterValues = Record<string, string | undefined>;

const legacyConnectors = (parameters: ApplicationParameterValues): SsoConnector[] => {
    const legacy = buildSsoConnector({
        path: parameters["sso-connector-path"],
        label: parameters["sso-connector-label"],
        logo: parameters["sso-connector-logo"],
    });

    return legacy === null ? [] : [legacy];
};

export const LEGACY_CONNECTOR_KEYS = ["sso-connector-path", "sso-connector-label", "sso-connector-logo"] as const;

export const legacyConnectorMatches = (
    parameters: ApplicationParameterValues,
    protocol: SsoProtocol,
    providerName: string,
): boolean =>
    legacyConnectors(parameters).some(
        (connector) => connector.protocol === protocol && connector.providerName === providerName,
    );

type ConnectorIdentity = {
    protocol: SsoProtocol;
    providerName: string;
    label: string;
};

export const attachConnectorLogos = (
    connectors: ConnectorIdentity[],
    parameters: ApplicationParameterValues,
): SsoConnector[] =>
    connectors
        .map((connector) => {
            const logo =
                parameters[buildConnectorParamKey("logo", connector.protocol, connector.providerName) ?? ""]?.trim();

            return {
                protocol: connector.protocol,
                providerName: connector.providerName,
                authorizationUrl: AUTHORIZATION_URL_BY_PROTOCOL[connector.protocol],
                label: connector.label,
                logo: logo !== undefined && logo !== "" ? logo : undefined,
            };
        })
        .sort((left, right) => left.label.localeCompare(right.label));

export type LoginLayout = {
    displayCredentialsForm: boolean;
    isSsoFirst: boolean;
    soleConnector: SsoConnector | null;
};

type ResolveLoginLayoutProps = {
    connectors: SsoConnector[];
    isCredentialsFormOpen: boolean;
    isLoading: boolean;
    loginMode: SsoLoginMode;
};

export const resolveLoginLayout = (props: ResolveLoginLayoutProps): LoginLayout => {
    const { connectors, isCredentialsFormOpen, isLoading, loginMode } = props;

    const isSsoFirst = connectors.length > 0 && loginMode === "sso-first";

    return {
        isSsoFirst,
        soleConnector: connectors.length === 1 ? connectors[0] : null,
        displayCredentialsForm: isLoading === false && (isSsoFirst === false || isCredentialsFormOpen),
    };
};
